import { z } from 'zod/v4';

/**
 * Public-facing representation of a company's stored Amfori credentials.
 *
 * SECURITY: the `clientSecret` field is INTENTIONALLY ABSENT — the plaintext
 * secret never leaves the service boundary. Only the `clientId`, the
 * `memberAmforiId` claim, and the `verifiedAt` timestamp are exposed.
 *
 * `clientSecretSet` is the visibility bit used by the UI to render whether
 * a credential currently has a secret stored ("•••••• Saved" vs empty).
 *
 * `lastSyncedAt` is the `completed_at` of the brand's most recent terminal-status
 * sync run (`COMPLETED`/`PARTIAL`), or `null` when no sync has finished yet — the
 * UI renders a "sync running" hint in that case rather than a timestamp.
 *
 * `revokedAt` is the ISO timestamp stamped when amfori rejected the stored
 * credentials (an exhausted 403 during a sync), or `null` when they are not
 * revoked. It drives the credentials-screen "Revoked — please re-enter" state
 * and is cleared when new credentials are saved.
 */
export const AmforiCredentialsResourceSchema = z
  .object({
    companyId: z.string(),
    clientId: z.string().nullable(),
    clientSecretSet: z.boolean(),
    memberAmforiId: z.string().nullable(),
    verifiedAt: z.string().nullable(),
    lastSyncedAt: z.string().nullable(),
    revokedAt: z.string().nullable(),
  })
  .strict();

export type AmforiCredentialsResource = z.infer<
  typeof AmforiCredentialsResourceSchema
>;

/**
 * Input accepted by `AmforiCredentialsService.upsert()`. The plaintext
 * `clientSecret` is in-memory only — it is encrypted before persistence and
 * is NEVER part of the returned resource.
 */
export const AmforiCredentialsUpsertInputSchema = z
  .object({
    clientId: z.string().min(1),
    clientSecret: z.string().min(1),
  })
  .strict();

export type AmforiCredentialsUpsertInput = z.infer<
  typeof AmforiCredentialsUpsertInputSchema
>;
